import React, { useEffect, useState } from 'react';
import { X, Check, Star } from 'lucide-react';

const PlatinumMembershipModal = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show modal after a short delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleStartTrial = () => {
    // Close the modal first
    handleClose();
    
    // Scroll to the packages section
    const packagesSection = document.getElementById('packages');
    if (packagesSection) {
      const headerHeight = document.querySelector('header')?.offsetHeight || 0;
      const scrollPadding = 10;
      const targetPosition = packagesSection.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: targetPosition - headerHeight - scrollPadding,
        behavior: 'smooth'
      });
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl p-3 sm:p-6 md:p-8 max-w-sm sm:max-w-2xl w-[95%] mx-auto transform transition-all duration-300 scale-100 animate-fade-in-up shadow-2xl max-h-[85vh] overflow-y-auto">
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X size={18} className="sm:w-6 sm:h-6" />
        </button>

        <div className="text-center">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-sky-100 text-sky-800 px-2.5 py-1 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-2 sm:mb-4">
            <Star size={12} className="sm:w-4 sm:h-4 fill-sky-600" />
            Premium Program
          </div>
          
          <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-1.5 sm:mb-2">
            Platinum Membership
          </h2>
          
          <p className="text-xs sm:text-base text-gray-600 mb-3 sm:mb-6 max-w-lg mx-auto">
            This all-encompassing program provides fully customized fitness and nutrition plans, priority coaching, and enhanced tracking tools to guide you toward your most ambitious goals. Experience premium support, tailored entirely to your needs.
          </p>
          
          <div className="mb-4 sm:mb-8">
            <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-2">
              <span className="text-3xl sm:text-5xl font-bold text-sky-600">$297</span>
              <div className="text-left">
                <span className="text-gray-500 text-xs sm:text-sm">per</span>
                <div className="text-gray-500 text-xs sm:text-sm">month</div>
              </div>
            </div>
          </div>
            
          <div className="space-y-2.5 sm:space-y-3 text-left bg-sky-50 p-3 sm:p-6 rounded-xl mb-4 sm:mb-8">
            <h3 className="font-semibold text-gray-900 mb-2.5 sm:mb-4 text-center text-sm sm:text-base">This includes:</h3>
            
            <div className="grid grid-cols-1 gap-2.5 sm:gap-3">
              <div className="flex items-start gap-2 sm:gap-3">
                <Check size={14} className="sm:w-[18px] sm:h-[18px] text-sky-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-gray-700 font-medium text-sm sm:text-base">Individualized Training Plan</span>
                  <div className="text-xs sm:text-sm text-gray-500">UNLIMITED Days/Week - $1400/month Value</div>
                </div>
              </div>
              
              <div className="flex items-start gap-2 sm:gap-3">
                <Check size={14} className="sm:w-[18px] sm:h-[18px] text-sky-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-gray-700 font-medium text-sm sm:text-base">Full Access to Fitness App</span>
                  <div className="text-xs sm:text-sm text-gray-500">$40/Month Value</div>
                </div>
              </div>
              
              <div className="flex items-start gap-2 sm:gap-3">
                <Check size={14} className="sm:w-[18px] sm:h-[18px] text-sky-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-gray-700 font-medium text-sm sm:text-base">UNLIMITED Text Support</span>
                  <div className="text-xs sm:text-sm text-gray-500">Habit + Accountability Coaching - $400/Month Value</div>
                </div>
              </div>
              
              <div className="flex items-start gap-2 sm:gap-3">
                <Check size={14} className="sm:w-[18px] sm:h-[18px] text-sky-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-gray-700 font-medium text-sm sm:text-base">UNLIMITED Nutrition Coaching</span>
                  <div className="text-xs sm:text-sm text-gray-500">$350/Month Value</div>
                </div>
              </div>
              
              <div className="flex items-start gap-2 sm:gap-3">
                <Check size={14} className="sm:w-[18px] sm:h-[18px] text-sky-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-gray-700 font-medium text-sm sm:text-base">Bi-weekly Virtual Check-ins</span>
                  <div className="text-xs sm:text-sm text-gray-500">$300/Month Value</div>
                </div>
              </div>
              
              <div className="flex items-start gap-2 sm:gap-3">
                <Check size={14} className="sm:w-[18px] sm:h-[18px] text-sky-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-gray-700 font-medium text-sm sm:text-base">Mobility Assessment</span>
                  <div className="text-xs sm:text-sm text-gray-500">$200 Value per Session</div>
                </div>
              </div>
              
              <div className="flex items-start gap-2 sm:gap-3">
                <Check size={14} className="sm:w-[18px] sm:h-[18px] text-sky-600 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-gray-700 font-medium text-sm sm:text-base">Injury/Rehab Consults</span>
                  <div className="text-xs sm:text-sm text-gray-500">$150 Value per Session</div>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleStartTrial}
            className="w-full bg-sky-600 text-white px-4 py-2.5 sm:px-8 sm:py-4 rounded-xl text-base sm:text-xl font-semibold hover:bg-sky-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
          >
            Start Trial
          </button>
          
          <p className="text-xs sm:text-sm text-gray-500 mt-2.5 sm:mt-4">
            Transform your fitness journey with our most comprehensive program
          </p>
        </div>
      </div>

      {/* Custom animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PlatinumMembershipModal; 